import { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Group,
  Stack,
  Text,
  Badge,
  RingProgress,
  Tooltip,
  ActionIcon,
  Divider,
  Collapse,
  Switch,
  Checkbox,
  Button,
} from "@mantine/core";

/** Animación simple de conteo hacia el valor destino */
function useCountUp(target, { duration = 700 } = {}) {
  const [val, setVal] = useState(Number(target) || 0);
  const fromRef = useRef(val);
  const startRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = fromRef.current;
    const to = Number(target) || 0;
    if (from === to) return;

    startRef.current = performance.now();
    const tick = (now) => {
      const p = Math.min((now - startRef.current) / duration, 1);
      const next = Math.round(from + (to - from) * p);
      setVal(next);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      fromRef.current = to;
    };
  }, [target, duration]);

  return val;
}

const formatNumber = (n) =>
  new Intl.NumberFormat("es-EC", { maximumFractionDigits: 0 }).format(n ?? 0);

const progressColor = (pct) => {
  if (pct >= 75) return "teal";
  if (pct >= 50) return "blue";
  if (pct >= 25) return "yellow";
  return "red";
};

/**
 * Card compacto con toggler + filtros
 * @param {{
 *  label?: string;
 *  total: number;         // registros filtrados
 *  totalAll?: number;     // total general
 *  loading?: boolean;
 *  searchTerm?: string;
 *  selectedCargos?: string[];
 *  birthdayRange?: [Date|null, Date|null];
 *  onlyActive?: boolean;
 *  onlyWithCode?: boolean;
 *  onChangeFilters?: (patch: {onlyActive?: boolean; onlyWithCode?: boolean}) => void;
 *  onResetFilters?: () => void;
 *  onRefresh?: () => void;
 * }} props
 */
export default function TotalRecordsToggleCard({
  label = "Registros",
  total = 0,
  totalAll = 0,
  loading = false,
  searchTerm,
  selectedCargos = [],
  birthdayRange = [null, null],
  onlyActive = false,
  onlyWithCode = false,
  onChangeFilters,
  onResetFilters,
  onRefresh,
  onShowTickets,
}) {
  const [opened, setOpened] = useState(false); // cerrado por defecto
  const animatedTotal = useCountUp(loading ? 0 : total);
  const pct = useMemo(
    () => (totalAll > 0 ? Math.round((total / totalAll) * 100) : 0),
    [total, totalAll]
  );

  // Tendencia simple
  const [trend, setTrend] = useState("flat"); // 'up' | 'down' | 'flat'
  const prevTotalRef = useRef(total);
  useEffect(() => {
    const prev = prevTotalRef.current;
    if (total > prev) setTrend("up");
    else if (total < prev) setTrend("down");
    else setTrend("flat");
    prevTotalRef.current = total;
  }, [total]);

  const [start, end] = birthdayRange || [null, null];
  const hasBirthday = Boolean(start && end);
  const hasSearch = Boolean(searchTerm && String(searchTerm).trim());
  const hasCargos = selectedCargos?.length > 0;

  const rangeLabel = useMemo(() => {
    const fmt = (d) =>
      d?.toLocaleDateString?.("es-EC", { day: "2-digit", month: "short" });
    return hasBirthday ? `${fmt(start)} — ${fmt(end)}` : "";
  }, [start, end, hasBirthday]);

  return (
    <Card shadow="sm" radius="lg" withBorder>
      {/* Header compacto (toggler) */}
      <Group
        position="apart"
        onClick={() => setOpened((o) => !o)}
        style={{ cursor: "pointer" }}
      >
        <Group spacing="xs">
          <Text size="lg" weight={700}>
            {label}
          </Text>
          <Text size="sm" color="dimmed">
            {formatNumber(total)} / {formatNumber(totalAll || total)}
          </Text>
          {trend === "up" && (
            <Tooltip label="Aumentó con los filtros">
              <Text size="sm" color="teal">
                <i className="fas fa-arrow-trend-up" />
              </Text>
            </Tooltip>
          )}
          {trend === "down" && (
            <Tooltip label="Disminuyó con los filtros">
              <Text size="sm" color="red">
                <i className="fas fa-arrow-trend-down" />
              </Text>
            </Tooltip>
          )}
        </Group>

        <Group spacing="xs" onClick={(e) => e.stopPropagation()}>
          {onResetFilters && (
            <Tooltip label="Limpiar filtros">
              <ActionIcon variant="light" onClick={onResetFilters}>
                <i className="fas fa-broom" />
              </ActionIcon>
            </Tooltip>
          )}
          {onRefresh && (
            <Tooltip label="Recargar">
              <ActionIcon variant="light" onClick={onRefresh}>
                <i className="fas fa-rotate-right" />
              </ActionIcon>
            </Tooltip>
          )}
          <ActionIcon variant="light" onClick={() => setOpened((o) => !o)}>
            <i className={`fas fa-chevron-${opened ? "up" : "down"}`} />
          </ActionIcon>
        </Group>
      </Group>

      {/* Badges de filtros activos (siempre visibles, compactos) */}
      <Group spacing="xs" mt="xs">
        {hasSearch && (
          <Badge variant="light" color="indigo" radius="sm">
            Búsqueda: “{searchTerm}”
          </Badge>
        )}
        {hasCargos && (
          <Badge variant="light" color="cyan" radius="sm">
            Cargos: {selectedCargos.length}
          </Badge>
        )}
        {hasBirthday && (
          <Badge variant="light" color="grape" radius="sm">
            Cumpleaños: {rangeLabel}
          </Badge>
        )}
        {onlyActive && (
          <Badge variant="light" color="teal" radius="sm">
            Solo activos
          </Badge>
        )}
        {onlyWithCode && (
          <Badge variant="light" color="violet" radius="sm">
            Con código
          </Badge>
        )}
      </Group>

      {/* Panel desplegable */}
      <Collapse in={opened}>
        <Divider my="md" />

        <Group position="apart" align="stretch" noWrap>
          <Group spacing="md" align="center" noWrap>
            <RingProgress
              size={110}
              thickness={12}
              sections={[{ value: pct, color: progressColor(pct) }]}
              label={
                <Stack spacing={0} align="center">
                  <Text weight={800} size="xl">
                    {formatNumber(animatedTotal)}
                  </Text>
                  <Text size="xs" color="dimmed">
                    de {formatNumber(totalAll || total)}
                  </Text>
                </Stack>
              }
            />
            <Stack spacing={2}>
              <Text size="sm" color="dimmed">
                Filtrados / Total
              </Text>
              <Text weight={700} size="lg">
                {formatNumber(total)} / {formatNumber(totalAll || total)}
              </Text>
              <Text size="sm" color={progressColor(pct)} weight={600}>
                {pct}% coinciden con los filtros
              </Text>
            </Stack>
          </Group>

          {/* Filtros rápidos dentro del card */}
          <Stack spacing="xs" align="flex-end">
            <Switch
              size="md"
              onLabel="ON"
              offLabel="OFF"
              label="Solo activos"
              checked={onlyActive}
              onChange={(e) =>
                onChangeFilters?.({ onlyActive: e.currentTarget.checked })
              }
            />
            <Checkbox
              label="Con código"
              checked={onlyWithCode}
              onChange={(e) =>
                onChangeFilters?.({ onlyWithCode: e.currentTarget.checked })
              }
            />
            <Button
              variant="light"
              size="xs"
              onClick={() => onShowTickets?.()}
              style={{ marginTop: 6 }}
              leftIcon={<i className="fa-solid fa-ticket" />}
            >
              Mostrar tickets
            </Button>
          </Stack>
        </Group>
      </Collapse>
    </Card>
  );
}
